import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  CreditCard,
  Check,
  Star,
  Sparkles,
  Shield,
  TrendingUp,
  Zap,
  ArrowRight,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const plans = [
  {
    id: 'diy',
    name: 'DIY',
    price: 29,
    description: 'For self-starters who want basic tools',
    features: [
      'Basic report import',
      'Self-guided disputes',
      'Academy access',
      'Email support'
    ],
    icon: Shield,
    popular: false
  },
  {
    id: 'ai_assisted',
    name: 'AI Assisted',
    price: 79,
    description: 'AI-powered analysis and recommendations',
    features: [
      'Everything in DIY',
      'AI report analysis',
      'Smart dispute letters',
      'Score simulator',
      'Priority support',
      'Utilization optimizer'
    ],
    icon: Sparkles,
    popular: true
  },
  {
    id: 'premium',
    name: 'Premium Funding Prep',
    price: 149,
    description: 'Complete funding readiness solution',
    features: [
      'Everything in AI Assisted',
      'Funding readiness coaching',
      '1-on-1 support sessions',
      'Priority processing',
      'Bank positioning strategy',
      'Lender matching'
    ],
    icon: TrendingUp,
    popular: false
  }
];

// Plan Card Component
const PlanCard = ({ plan, currentPlan, onSelect, loading }) => {
  const Icon = plan.icon;
  const isCurrentPlan = currentPlan === plan.id;
  const isPopular = plan.popular;

  return (
    <div 
      className={`elite-card p-6 relative ${
        isPopular ? 'border-2 border-emerald-500 shadow-lg shadow-emerald-500/10' : ''
      } ${isCurrentPlan ? 'bg-emerald-50' : ''}`}
      data-testid={`plan-${plan.id}`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
            <Star size={12} />
            Most Popular
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          isPopular ? 'bg-emerald-100' : 'bg-slate-100'
        }`}>
          <Icon size={24} className={isPopular ? 'text-emerald-600' : 'text-slate-600'} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-stone-900">{plan.name}</h3>
          {isCurrentPlan && (
            <span className="text-xs text-emerald-600 font-medium">Current Plan</span>
          )}
        </div>
      </div>

      <div className="mb-4">
        <span className="text-4xl font-bold text-stone-900">${plan.price}</span>
        <span className="text-slate-500">/month</span>
      </div>

      <p className="text-sm text-slate-500 mb-6">{plan.description}</p>

      <ul className="space-y-3 mb-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
            <span className="text-slate-600">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={() => onSelect(plan.id)}
        disabled={isCurrentPlan || loading}
        className={`w-full ${isPopular ? 'btn-primary' : 'btn-secondary'}`}
        data-testid={`select-plan-${plan.id}`}
      >
        {loading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : isCurrentPlan ? (
          <>
            <CheckCircle size={18} className="mr-2" />
            Current Plan
          </>
        ) : (
          <>
            {currentPlan ? 'Switch Plan' : 'Get Started'}
            <ArrowRight size={18} className="ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

// Billing Page Component
const Billing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeaders, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    // Check for payment callback
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    
    const maxAttempts = 5;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/billing/status/${sessionId}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });

        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your plan has been upgraded.');
          // Refresh user data
          updateUser({ plan: response.data.plan_id });
          navigate('/billing', { replace: true });
          return;
        }

        if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          navigate('/billing', { replace: true });
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.error('Payment verification timed out. Please check your email for confirmation.');
          navigate('/billing', { replace: true });
        }
      } catch (error) {
        console.error('Payment status error:', error);
        toast.error('Failed to verify payment status.');
        navigate('/billing', { replace: true });
      }
    };

    poll();
    setCheckingPayment(false);
  };

  const handleSelectPlan = async (planId) => {
    if (planId === user?.plan) return;

    setLoading(true);
    try {
      // Get current origin for redirect URLs
      const originUrl = window.location.origin;

      const response = await axios.post(`${API}/billing/checkout`,
        { plan_id: planId, origin_url: originUrl },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-900">Verifying Payment...</h2>
          <p className="text-slate-500 mt-2">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="billing-page">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-stone-900 mb-3">Choose Your Plan</h1>
        <p className="text-slate-500 max-w-xl mx-auto">
          Select the plan that best fits your credit repair and funding goals. 
          Upgrade or downgrade anytime.
        </p>
      </div>

      {/* Current Plan Banner */}
      {user?.plan && user.plan !== 'free' && (
        <div className="elite-card p-4 mb-8 bg-emerald-50 border-emerald-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-emerald-600" size={24} />
              <div>
                <p className="font-semibold text-stone-900">Current Plan: {plans.find(p => p.id === user.plan)?.name || 'Free'}</p>
                <p className="text-sm text-slate-500">Your subscription is active</p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              Manage Subscription
            </Button>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currentPlan={user?.plan}
            onSelect={handleSelectPlan}
            loading={loading}
          />
        ))}
      </div>

      {/* FAQ Section */}
      <div className="mt-12 elite-card p-6">
        <h2 className="text-lg font-semibold text-stone-900 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-stone-900">Can I cancel anytime?</h3>
            <p className="text-sm text-slate-500 mt-1">
              Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-stone-900">What payment methods do you accept?</h3>
            <p className="text-sm text-slate-500 mt-1">
              We accept all major credit cards through our secure payment partner Stripe.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-stone-900">Can I switch plans?</h3>
            <p className="text-sm text-slate-500 mt-1">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="flex items-center justify-center gap-8 mt-8 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-emerald-500" />
          <span>Secure Payment</span>
        </div>
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-emerald-500" />
          <span>Instant Access</span>
        </div>
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-emerald-500" />
          <span>Cancel Anytime</span>
        </div>
      </div>
    </div>
  );
};

export default Billing;
